// Sprint CRM-5 — vista a righe: i dati della scheda in colonna come colonne; scelta, ordine e larghezze con il componente
// delle liste del gestionale (VehicleColumnsButton + lib/listaColonne), ricordati nel browser per pipeline.
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatEUR } from "@/lib/utils";
import { Cella, fmtData, useLarghezzeAuto, LARG_MIN } from "@/lib/listaColonne";
import VehicleColumnsButton from "@/components/VehicleColumnsButton";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowDown, ArrowUp } from "lucide-react";
import { StatoBadge, ContatoreContatto, Pallino, TIPI_SOGGETTO, fmtTelefono, stileEtichetta } from "./crmComuni";

// «posizione/totale · nome»; fase archiviata: «— · nome (archiviata)»
export const testoFase = (o) => (!o.fase ? "—" : o.fase_posizione
  ? `${o.fase_posizione}/${o.fasi_attive} · ${o.fase.nome}` : `— · ${o.fase.nome} (archiviata)`);

const primo = (l, f = (x) => x) => (l?.length ? `${f(l[0])}${l.length > 1 ? ` +${l.length - 1}` : ""}` : "—");

export const COLONNE_CRM = [
  { key: "descrizione", label: "Descrizione", sort: "descrizione", v: (o) => o.descrizione },
  { key: "titolare", label: "Titolare", sort: "titolare", v: (o, c) => (o.libera ? c.Libera({ o }) : <span className="inline-flex items-center gap-1.5">
    <Pallino id={o.titolare_id} nome={o.titolare_nome} testid={`crm-riga-pallino-${o.id}`} />{o.titolare_nome || "—"}</span>) },
  { key: "contatto", label: "Soggetto", sort: "contatto", v: (o) => o.contatto?.nome || "—" },
  { key: "tipo_soggetto", label: "Privato/Azienda", v: (o) => TIPI_SOGGETTO[o.contatto?.tipo_soggetto] || "—" },
  { key: "telefono", label: "Telefono", v: (o) => <span className="font-mono">{primo(o.contatto?.telefoni, fmtTelefono)}</span> },
  { key: "email", label: "Email", v: (o) => primo(o.contatto?.email) },
  { key: "conteggi", label: "Opportunità del contatto", v: (o) => <ContatoreContatto conteggi={o.contatto?.conteggi} testid={`crm-riga-contatore-${o.id}`} /> },
  { key: "fase", label: "Fase", sort: "fase", v: (o, c) => <span className="inline-flex items-center gap-1.5" data-testid={`crm-riga-fase-${o.id}`}>{testoFase(o)}{c.Ferma({ o })}</span> },
  { key: "stato", label: "Stato", sort: "stato", v: (o) => <StatoBadge stato={o.stato} /> },
  { key: "prezzo_vendita", label: "Prezzo di vendita", sort: "prezzo_vendita", right: true, v: (o, c) => c.PrezzoVendita({ o }) },
  { key: "prezzo_web", label: "Prezzo web", sort: "prezzo_web", right: true, v: (o) => <span className="font-mono">{formatEUR(o.prezzo_web_mostrato)}</span> },
  { key: "created_at", label: "Creata", sort: "created_at", v: (o) => fmtData(o.created_at) },
  { key: "updated_at", label: "Aggiornata", sort: "updated_at", v: (o) => fmtData(o.updated_at) },
  { key: "giorni", label: "Giorni nella fase", sort: "giorni", right: true, v: (o) => (o.giorni_in_fase == null ? "—" : o.giorni_in_fase) },
  { key: "prossima", label: "Prossima attività", v: (o) => (o.prossima_attivita ? `${fmtData(o.prossima_attivita.inizio)} – ${o.prossima_attivita.titolo}` : "—") },
  { key: "fonte", label: "Fonte", sort: "fonte", v: (o) => o.fonte_nome || "—" },
  { key: "etichette", label: "Etichette", v: (o) => (o.etichette_dati?.length ? <span className="inline-flex gap-1">{o.etichette_dati.map((e) =>
    <span key={e.id} className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={stileEtichetta(e.colore)}>{e.nome}</span>)}</span> : "—") },
  { key: "sede", label: "Sede", v: (o) => o.sede_nome || "—" },
  { key: "pipeline", label: "Pipeline", v: (o) => o.pipeline_nome || "—" },
  { key: "motivo", label: "Motivo perdita", v: (o) => (o.stato === "persa" ? o.motivo_perdita_nome || "Senza motivo" : "—") },  // Sprint CRM-6
];
// Le colonne di prima restano accese di partenza, le nuove si accendono dal pulsante «Colonne»
export const VISIBILI_PARTENZA = ["descrizione", "contatto", "fase", "stato", "prezzo_vendita", "titolare", "fonte", "created_at", "updated_at"];
const ORDINE_PARTENZA = ["descrizione", "contatto", "fase", "stato", "prezzo_vendita", "titolare", "fonte", "created_at", "updated_at"];
const lsColonne = (pid) => `crm-righe-colonne-${pid}`;
const PARTENZA = { order: ORDINE_PARTENZA, visible: VISIBILI_PARTENZA };
export function leggiColonne(pid, partenza = PARTENZA) {
  try { return { ...partenza, widths: {}, ...JSON.parse(localStorage.getItem(lsColonne(pid)) || "{}") }; }
  catch { return { ...partenza, widths: {} }; }
}

function Intestazione({ c, w, sort, onSort, onLarg }) {
  const resize = (e) => {
    e.stopPropagation();
    const x0 = e.clientX, w0 = e.currentTarget.parentElement.getBoundingClientRect().width;
    const muovi = (ev) => onLarg(c.key, Math.max(LARG_MIN, w0 + ev.clientX - x0));
    const fine = () => { window.removeEventListener("mousemove", muovi); window.removeEventListener("mouseup", fine); };
    window.addEventListener("mousemove", muovi); window.addEventListener("mouseup", fine);
  };
  return (
    <th data-col={c.key} style={w ? { width: w } : undefined} onClick={c.sort ? () => onSort(c.sort) : undefined}
      className={`px-3 py-2 relative whitespace-nowrap overflow-hidden select-none ${c.sort ? "cursor-pointer hover:bg-slate-100" : ""} ${c.right ? "text-right" : ""}`}
      data-testid={`crm-col-h-${c.key}`}>
      <span className="th-label inline-flex items-center gap-1">{c.label}
        {sort.by === c.sort && (sort.order === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}</span>
      <span onMouseDown={resize} onClick={(e) => e.stopPropagation()} data-testid={`crm-col-resize-${c.key}`}
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-300 active:bg-blue-500" />
    </th>
  );
}

// Sprint CRM-6 — riusata nella tab «Lead» del veicolo: senza selezione (sel assente), colonne di partenza proprie
export default function VistaRigheCrm({ items, sort, setSort, sel, pid, celle, partenza = PARTENZA, vuoto = "Nessuna opportunità" }) {
  const nav = useNavigate();
  const [cfg, setCfgState] = useState(() => leggiColonne(pid, partenza));
  const setCfg = (x) => { const n = { ...cfg, ...x }; setCfgState(n); try { localStorage.setItem(lsColonne(pid), JSON.stringify(n)); } catch {} };
  const perKey = Object.fromEntries(COLONNE_CRM.map((c) => [c.key, c]));
  const ordine = [...cfg.order.filter((k) => perKey[k]), ...COLONNE_CRM.map((c) => c.key).filter((k) => !cfg.order.includes(k))];
  const colonne = ordine.filter((k) => cfg.visible.includes(k)).map((k) => perKey[k]);
  const ref = useRef(null);
  const auto = useLarghezzeAuto(ref, [items, colonne.map((c) => c.key).join()]);
  const larg = (c) => cfg.widths[c.key] ?? auto[c.key];
  const clic = (k) => setSort(sort.by === k ? { by: k, order: sort.order === "asc" ? "desc" : "asc" } : { by: k, order: "asc" });
  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <VehicleColumnsButton allColumns={COLONNE_CRM} order={ordine} visible={cfg.visible} onChange={({ order, visible }) => setCfg({ order, visible })}
          onReset={() => setCfg({ order: partenza.order, visible: partenza.visible })} onResetWidths={() => setCfg({ widths: {} })} />
      </div>
      <div className="overflow-x-auto">
        <table ref={ref} className="w-full text-sm lista-uniforme" style={{ tableLayout: "fixed" }} data-testid="crm-tabella">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-[12px] text-slate-600 uppercase tracking-wide">
              {sel && <th className="px-3 py-2 w-10"><Checkbox checked={sel.tutte} onCheckedChange={sel.togliTutte} data-testid="crm-sel-tutte" /></th>}
              {colonne.map((c) => <Intestazione key={c.key} c={c} w={larg(c)} sort={sort} onSort={clic}
                onLarg={(k, w) => setCfg({ widths: { ...cfg.widths, [k]: Math.round(w) } })} />)}
            </tr>
          </thead>
          <tbody>
            {!items.length && <tr><td colSpan={colonne.length + 1} className="px-3 py-8 text-center text-slate-400">{vuoto}</td></tr>}
            {items.map((o) => (
              <tr key={o.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/crm/opportunita/${o.id}`)} data-testid={`crm-riga-${o.id}`}>
                {sel && <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.ids.includes(o.id)}
                  onCheckedChange={() => sel.tog(o.id)} data-testid={`crm-sel-${o.id}`} /></td>}
                {colonne.map((c) => <td key={c.key} data-col={c.key} className={`px-3 py-2 ${c.right ? "text-right" : ""}`}
                  data-testid={`crm-cella-${c.key}-${o.id}`}><Cella>{c.v(o, celle)}</Cella></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
